import React, { useState } from 'react';

import { MaterialIcon } from '@ninox/ninox-components';
import { Size } from '@ninox/ninox-components/theme';

import { translations } from '../../locale/translations';
import { validatePhone } from '../../utils/util';
import { countries, sizeOptions } from '../constants';
import { SignUpProps } from '../Credentials';
import { BusinessUser } from '../Credentials.types';
import { Button, Combobox, ErrorMessage, FormControl, FormErrorMessage, Input, Title } from '../shared.styles';
import { Row } from './BusinessSignup.styles';

export interface BusinessSignupProps extends SignUpProps {
    showLogout?: boolean;
}

export const BusinessSignup = ({
    lang,
    email,
    userId,
    signupType,
    showLogout,
    mixPanelTracker,
    googleTracker,
    hubspotTracker,
    updateUserDetails,
    logout,
    afterLogin
}: BusinessSignupProps) => {
    const [formState, setFormState] = useState<BusinessUser>({
        displayName: '',
        company: '',
        telephone: '',
        country: 'Germany',
        businessSize: ''
    });
    const [showErrors, setShowErrors] = useState(false);
    const [currentError, setCurrentError] = useState<string | null>(null);
    const locale = translations[lang];
    const { displayName, company, country, telephone, businessSize } = formState;
    const isNameValid = !!displayName.length;
    const isCompanyValid = !!company.length;
    const isTelephoneValid = validatePhone(telephone);
    const isBSValid = !!businessSize.length;
    const isValid = isNameValid && isCompanyValid && isTelephoneValid && isBSValid;

    const handleState = (field: keyof BusinessUser, val: string) => {
        setFormState({ ...formState, [field]: val });
        setShowErrors(false);
    };

    const updateUserProfile = async () => {
        if (!isValid) {
            return setShowErrors(true);
        }

        try {
            await mixPanelTracker.track('ProfileForm', 'Click', { signupType: signupType });
            await updateUserDetails(formState);

            await hubspotTracker.sendTrialSignupFormDataToHubspot(
                {
                    ...formState,
                    email: email,
                    userId: userId,
                    user_locale: lang
                },
                'Ninox Business Trial Signup'
            );

            await googleTracker.trackSignupDetails(userId);
            await mixPanelTracker.track('ProfileForm', 'Success', { signupType: signupType });

            afterLogin({ justSignedUp: true });
        } catch (error) {
            await mixPanelTracker.track('ProfileForm', 'Error', { signupType: signupType });

            return setCurrentError((error as any).message);
        }
    };

    const handleSwitchAccount = () => {
        logout();
    };

    return (
        <>
            <Title>{locale.signUpHeader3}</Title>

            <FormControl required>
                <label htmlFor="fullName">{locale.fullName}</label>
                <Input
                    aria-invalid={showErrors && !isNameValid}
                    id="fullName"
                    required
                    type="text"
                    value={displayName}
                    onChange={(e) => handleState('displayName', e.target.value)}
                    onFocus={(e) => {
                        e.target.scrollIntoView({ block: 'center', inline: 'center' });
                    }}
                />
                {showErrors && !isNameValid && (
                    <ErrorMessage>
                        <MaterialIcon filling={0} icon="error" size={Size.xs} />
                        {locale.thisFieldsAreRequired}
                    </ErrorMessage>
                )}
            </FormControl>

            <Row>
                <FormControl required>
                    <label htmlFor="company">{locale.company}</label>
                    <Input
                        aria-invalid={showErrors && !isCompanyValid}
                        id="company"
                        required
                        type="text"
                        value={company}
                        onChange={(e) => handleState('company', e.target.value)}
                        onFocus={(e) => {
                            e.target.scrollIntoView({ block: 'center', inline: 'center' });
                        }}
                    />
                    {showErrors && !isCompanyValid && (
                        <ErrorMessage>
                            <MaterialIcon filling={0} icon="error" size={Size.xs} />
                            {locale.thisFieldsAreRequired}
                        </ErrorMessage>
                    )}
                </FormControl>
                <FormControl required>
                    <label htmlFor="numberOfEmployees">{locale.numberOfEmployees}</label>
                    <Combobox
                        id="numberOfEmployees"
                        name="numberOfEmployees"
                        options={sizeOptions.map((sizeOption) => ({
                            value: sizeOption.id,
                            label: sizeOption.id
                        }))}
                        placeholder={locale.select}
                        required
                        value={businessSize}
                        onChange={(size: string) => handleState('businessSize', String(size))}
                    />
                    {showErrors && !isBSValid && (
                        <ErrorMessage>
                            <MaterialIcon filling={0} icon="error" size={Size.xs} />
                            {locale.thisFieldsAreRequired}
                        </ErrorMessage>
                    )}
                </FormControl>
            </Row>

            <FormControl required>
                <label htmlFor="country">{locale.country}</label>
                <Combobox
                    id="country"
                    name="country"
                    options={countries.map(({ name }) => ({ value: name, label: name }))}
                    placeholder={locale.select}
                    required
                    value={country}
                    onChange={(val: string) => handleState('country', String(val))}
                />
            </FormControl>
            <FormControl required>
                <label htmlFor="telephone">{locale.workPhone}</label>
                <Input
                    aria-invalid={showErrors && !isTelephoneValid}
                    id="telephone"
                    required
                    type="tel"
                    value={telephone}
                    onChange={(e) => handleState('telephone', e.target.value)}
                    onFocus={(e) => {
                        e.target.scrollIntoView({ block: 'center', inline: 'center' });
                    }}
                />
                {showErrors && !isTelephoneValid && (
                    <ErrorMessage>
                        <MaterialIcon filling={0} icon="error" size={Size.xs} />
                        {telephone.length > 0 ? locale.phoneError : locale.thisFieldsAreRequired}
                    </ErrorMessage>
                )}
            </FormControl>

            {showErrors && currentError && <FormErrorMessage>{currentError}</FormErrorMessage>}

            <Button data-testid="save-profile" emphasis="accent" size="m" onClick={updateUserProfile}>
                {locale.saveProfile}
            </Button>

            {showLogout && (
                <Button emphasis="quaternary" size="m" onClick={handleSwitchAccount}>
                    {locale.switchAccount}
                </Button>
            )}
        </>
    );
};
